import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip,
  Tabs, Tab, IconButton, Tooltip, Badge, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as TaskIcon,
  Group as TeamIcon,
  AttachMoney as BudgetIcon,
  Schedule as TimeIcon,
  CheckCircle as CompletedIcon,
  Pending as PendingIcon,
  Warning as OverdueIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import { GradientButton, GlassButton } from '../common/ModernButton';
import { useLanguage } from '../../contexts/LanguageContext';
import TaskList from './TaskList';
import TaskForm from './TaskForm';
import BudgetOverview from './BudgetOverview';
import ProjectEstimator from './ProjectEstimator';
import TeamWorkload from './TeamWorkload';

const TaskDashboard = () => {
  const { translate } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');

  // Mock data - replace with API calls
  const [tasks, setTasks] = useState([
    {
      id: 1,
      title: 'Setup UniFi Access Control',
      description: 'Configure door access for new employees',
      status: 'in_progress',
      priority: 'high',
      assignee: 'John Doe',
      dueDate: '2024-01-15',
      estimatedHours: 8,
      actualHours: 4,
      budget: 500,
      spent: 200,
      project: 'Security System',
      tags: ['unifi', 'access-control', 'security']
    },
    {
      id: 2,
      title: 'Database Schema Update',
      description: 'Add new tables for task management system',
      status: 'completed',
      priority: 'medium',
      assignee: 'Jane Smith',
      dueDate: '2024-01-10',
      estimatedHours: 12,
      actualHours: 10,
      budget: 800,
      spent: 650,
      project: 'System Upgrade',
      tags: ['database', 'backend', 'schema']
    },
    {
      id: 3,
      title: 'Frontend Task Components',
      description: 'Create React components for task management',
      status: 'pending',
      priority: 'high',
      assignee: 'Mike Johnson',
      dueDate: '2024-01-20',
      estimatedHours: 16,
      actualHours: 0,
      budget: 1000,
      spent: 0,
      project: 'System Upgrade',
      tags: ['frontend', 'react', 'components']
    }
  ]);

  const [employees, setEmployees] = useState([
    { id: 1, name: 'John Doe', role: 'System Administrator', department: 'IT' },
    { id: 2, name: 'Jane Smith', role: 'Backend Developer', department: 'Engineering' },
    { id: 3, name: 'Mike Johnson', role: 'Frontend Developer', department: 'Engineering' },
    { id: 4, name: 'Sarah Wilson', role: 'Project Manager', department: 'Management' }
  ]);

  const [projects, setProjects] = useState([
    { id: 1, name: 'Security System', budget: 5000, spent: 1200 },
    { id: 2, name: 'System Upgrade', budget: 8000, spent: 3200 },
    { id: 3, name: 'Mobile App', budget: 15000, spent: 8500 }
  ]);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/tasks');
      // const data = await response.json();
      // setTasks(data);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCreateTask = () => {
    setSelectedTask(null);
    setShowTaskForm(true);
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setShowTaskForm(true);
  };

  const handleSaveTask = async (taskData) => {
    try {
      if (selectedTask) {
        // Update existing task
        const updatedTasks = tasks.map(task => 
          task.id === selectedTask.id ? { ...task, ...taskData } : task
        );
        setTasks(updatedTasks);
      } else {
        // Create new task
        const newTask = {
          id: Date.now(),
          ...taskData,
          status: 'pending',
          actualHours: 0,
          spent: 0
        };
        setTasks([...tasks, newTask]);
      }
      setShowTaskForm(false);
      setSelectedTask(null);
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const updatedTasks = tasks.filter(task => task.id !== taskId);
      setTasks(updatedTasks);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'pending': return 'warning';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CompletedIcon />;
      case 'in_progress': return <PendingIcon />;
      case 'pending': return <PendingIcon />;
      case 'overdue': return <OverdueIcon />;
      default: return <PendingIcon />;
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filterStatus !== 'all' && task.status !== filterStatus) return false;
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    if (filterAssignee !== 'all' && task.assignee !== filterAssignee) return false;
    return true;
  });

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    pending: tasks.filter(t => t.status === 'pending').length,
    overdue: tasks.filter(t => t.status === 'overdue').length,
    totalBudget: tasks.reduce((sum, t) => sum + t.budget, 0),
    totalSpent: tasks.reduce((sum, t) => sum + t.spent, 0),
    totalEstimatedHours: tasks.reduce((sum, t) => sum + t.estimatedHours, 0),
    totalActualHours: tasks.reduce((sum, t) => sum + t.actualHours, 0)
  };

  const tabLabels = [
    { label: 'Tasks', icon: <TaskIcon />, count: stats.total },
    { label: 'Budget', icon: <BudgetIcon />, count: projects.length },
    { label: 'Estimator', icon: <TrendingUpIcon /> },
    { label: 'Team', icon: <TeamIcon />, count: employees.length }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <TaskList
            tasks={tasks}
            employees={employees}
            onEditTask={handleEditTask}
            onDeleteTask={handleDeleteTask}
            filterStatus={filterStatus}
            filterPriority={filterPriority}
            filterAssignee={filterAssignee}
            onFilterChange={(type, value) => {
              switch (type) {
                case 'status': setFilterStatus(value); break;
                case 'priority': setFilterPriority(value); break;
                case 'assignee': setFilterAssignee(value); break;
                default: break;
              }
            }}
          />
        );
      case 1:
        return <BudgetOverview tasks={tasks} projects={projects} />;
      case 2:
        return <ProjectEstimator />;
      case 3:
        return <TeamWorkload tasks={tasks} employees={employees} />;
      default:
        return null;
    }
  };

  return (
    <ModernLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            {translate('taskDashboard')}
          </Typography>
          <GradientButton
            onClick={handleCreateTask}
            startIcon={<AddIcon />}
            sx={{ borderRadius: 2 }}
          >
            {translate('createTask')}
          </GradientButton>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {tasks.length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {translate('tasks')}
                    </Typography>
                  </Box>
                  <TaskIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              borderRadius: 3
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {tasks.filter(t => t.status === 'completed').length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {translate('completed')}
                    </Typography>
                  </Box>
                  <CompletedIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              borderRadius: 3
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {tasks.filter(t => t.status === 'in_progress').length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {translate('inProgress')}
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              borderRadius: 3
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {tasks.filter(t => t.status === 'overdue').length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {translate('overdue')}
                    </Typography>
                  </Box>
                  <OverdueIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 64,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textTransform: 'none'
                }
              }}
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TaskIcon />
                    {translate('taskList')}
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BudgetIcon />
                    {translate('budgetOverview')}
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon />
                    {translate('projectEstimator')}
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TeamIcon />
                    {translate('teamWorkload')}
                  </Box>
                } 
              />
            </Tabs>
          </Box>
          
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              renderTabContent()
            )}
          </Box>
        </Card>

        {/* Task Form Dialog */}
        <Dialog 
          open={showTaskForm} 
          onClose={() => setShowTaskForm(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 'bold'
          }}>
            {selectedTask ? translate('editTask') : translate('createTask')}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <TaskForm
              task={selectedTask}
              employees={employees}
              projects={projects}
              onSave={handleSaveTask}
              onCancel={() => setShowTaskForm(false)}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </ModernLayout>
  );
};

export default TaskDashboard; 